import React, { useState, useEffect } from 'react';
import '../styles/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  // Contact information
  const contactInfo = [
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Our Address',
      content: '272, Mill Road<br>Irakkamam - 03<br>Sri Lanka',
    },
    {
      icon: 'fas fa-phone-alt',
      title: 'Phone Number',
      content: '+94 750771507',
    },
    {
      icon: 'fas fa-envelope',
      title: 'Email Address',
      content: 'info@fahple.com<br>support@fahple.com',
    },
    {
      icon: 'fas fa-clock',
      title: 'Business Hours',
      content:
        'Monday - Friday: 9:00 AM - 6:00 PM<br>Saturday: 9:00 AM - 1:00 PM<br>Sunday: Closed',
    },
  ];

  // Social media links
  const socialLinks = [
    { icon: 'fab fa-facebook-f', url: '#' },
    { icon: 'fab fa-twitter', url: '#' },
    { icon: 'fab fa-linkedin-in', url: '#' },
    { icon: 'fab fa-instagram', url: '#' },
  ];

  // Subject options
  const subjectOptions = [
    { value: '', label: 'Select a subject' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'advertising', label: 'Advertising Services' },
    { value: 'job-portal', label: 'Job Portal Services' },
    { value: 'support', label: 'Technical Support' },
    { value: 'feedback', label: 'Feedback' },
  ];

  // Features grid data
  const features = [
    {
      icon: 'fas fa-bolt',
      title: 'Quick Support',
      description:
        'Get fast answers and guidance from our dedicated support team. We respond within 24 hours.',
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Expert Advice',
      description:
        'Receive professional recommendations tailored to your specific advertising needs and goals.',
    },
    {
      icon: 'fas fa-comments',
      title: 'Seamless Communication',
      description:
        'Multiple ways to reach us - phone, email, or live chat. We make communication easy and effective.',
    },
  ];

  useEffect(() => {
    // Initialize character counter
    setCharCount(formData.message.length);

    // Load Font Awesome if not already loaded
    loadFontAwesome();
  }, [formData.message]);

  const loadFontAwesome = () => {
    // Check if Font Awesome is already loaded
    if (!document.querySelector('#font-awesome')) {
      const link = document.createElement('link');
      link.id = 'font-awesome';
      link.rel = 'stylesheet';
      link.href =
        'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(link);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const validateForm = () => {
    const newErrors = [];

    if (!formData.name.trim()) newErrors.push('Name is required');
    if (!formData.email.trim()) newErrors.push('Email is required');
    if (!formData.message.trim()) newErrors.push('Message is required');

    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.push('Please enter a valid email address');
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();

    if (newErrors.length > 0) {
      setErrors(newErrors);

      // Scroll to first error
      const firstErrorElement = document.querySelector('.form-control.error');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // In a real application, you would send the data to your backend here
      console.log('Form submitted:', formData);

      setSuccessMessage(
        "Thank you for your message! We'll get back to you within 24 hours."
      );
      setErrors([]);

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      setErrors(['Something went wrong. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCharCounterClass = () => {
    if (charCount > 450) return 'char-counter error';
    if (charCount > 400) return 'char-counter warning';
    return 'char-counter';
  };

  const getInputClass = (fieldName) => {
    const baseClass = 'form-control';
    const hasValue = formData[fieldName].trim() !== '';
    const hasError = errors.some(
      (error) =>
        error.toLowerCase().includes(fieldName) ||
        (fieldName === 'email' && error.includes('email'))
    );

    if (hasError) return `${baseClass} error`;
    if (hasValue) return `${baseClass} filled`;
    return baseClass;
  };

  return (
    <>
      {/* Hero Section */}
      <section className='contact-hero'>
        <div className='container'>
          <h1>Contact Fahple</h1>
          <p>
            We'd love to hear from you! Whether you have questions, feedback, or
            want to start a new advertising campaign, our team is ready to
            assist.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className='contact-section'>
        <div className='contact-container'>
          {/* Contact Information */}
          <div className='contact-info'>
            <h2>Get in Touch</h2>

            {contactInfo.map((info, index) => (
              <div key={index} className='info-item'>
                <div className='info-icon'>
                  <i className={info.icon} aria-hidden='true'></i>
                </div>
                <div className='info-content'>
                  <h3>{info.title}</h3>
                  <p dangerouslySetInnerHTML={{ __html: info.content }}></p>
                </div>
              </div>
            ))}

            {/* Social Media Links */}
            <div className='social-section'>
              <h3>Follow Us</h3>
              <div className='social-links'>
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className='social-link'
                    target='_blank'
                    rel='noopener noreferrer'
                    aria-label={`Follow us on ${
                      social.icon.includes('facebook')
                        ? 'Facebook'
                        : social.icon.includes('twitter')
                        ? 'Twitter'
                        : social.icon.includes('linkedin')
                        ? 'LinkedIn'
                        : 'Instagram'
                    }`}
                  >
                    <i className={social.icon} aria-hidden='true'></i>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className='contact-form'>
            <h2>Send us a Message</h2>

            {successMessage && (
              <div className='alert alert-success' role='alert'>
                <i className='fas fa-check-circle' aria-hidden='true'></i>
                {successMessage}
              </div>
            )}

            {errors.length > 0 && (
              <div className='alert alert-error' role='alert'>
                <i
                  className='fas fa-exclamation-triangle'
                  aria-hidden='true'
                ></i>
                <div className='error-list'>
                  {errors.map((error, index) => (
                    <p key={index} style={{ margin: '5px 0' }}>
                      {error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} id='contactForm' noValidate>
              <div className='form-group'>
                <label className='form-label' htmlFor='name'>
                  <i className='fas fa-user' aria-hidden='true'></i>
                  Full Name *
                </label>
                <input
                  type='text'
                  className={getInputClass('name')}
                  id='name'
                  name='name'
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  aria-required='true'
                />
              </div>

              <div className='form-group'>
                <label className='form-label' htmlFor='email'>
                  <i className='fas fa-envelope' aria-hidden='true'></i>
                  Email Address *
                </label>
                <input
                  type='email'
                  className={getInputClass('email')}
                  id='email'
                  name='email'
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  aria-required='true'
                />
              </div>

              <div className='form-group'>
                <label className='form-label' htmlFor='phone'>
                  <i className='fas fa-phone' aria-hidden='true'></i>
                  Phone Number
                </label>
                <input
                  type='tel'
                  className={getInputClass('phone')}
                  id='phone'
                  name='phone'
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className='form-group'>
                <label className='form-label' htmlFor='subject'>
                  <i className='fas fa-tag' aria-hidden='true'></i>
                  Subject
                </label>
                <select
                  className={getInputClass('subject')}
                  id='subject'
                  name='subject'
                  value={formData.subject}
                  onChange={handleInputChange}
                >
                  {subjectOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className='form-group'>
                <label className='form-label' htmlFor='message'>
                  <i className='fas fa-comment' aria-hidden='true'></i>
                  Message *
                </label>
                <textarea
                  className={getInputClass('message')}
                  id='message'
                  name='message'
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  maxLength='500'
                  rows='5'
                  aria-required='true'
                ></textarea>
                <div className={getCharCounterClass()}>
                  <span>{charCount}/500 characters</span>
                </div>
              </div>

              <button
                type='submit'
                className={`btn-submit ${isSubmitting ? 'loading' : ''}`}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i
                      className='fas fa-spinner fa-spin'
                      aria-hidden='true'
                    ></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className='fas fa-paper-plane' aria-hidden='true'></i>
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className='map-section'>
        <div className='map-container'>
          <h2>
            <i className='fas fa-map' aria-hidden='true'></i>
            Find Us
          </h2>
          <div className='map-wrapper'>
            <iframe
              src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3961.950234045575!2d81.0009!3d6.9276!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwNTUnMzkuNCJOIDgxwrAwMCcwMy4xIkU!5e0!3m2!1sen!2slk!4v1234567890'
              allowFullScreen=''
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
              title='Fahple Location Map'
              aria-label='Interactive map showing Fahple office location'
            ></iframe>
          </div>
        </div>
      </section>

      {/* Why Contact Section */}
      <section className='why-contact'>
        <div className='container'>
          <h2>Why Contact Fahple?</h2>
          <p>Discover the benefits of reaching out to our dedicated team</p>

          <div className='features-grid'>
            {features.map((feature, index) => (
              <div key={index} className='feature-card'>
                <div className='feature-icon'>
                  <i className={feature.icon} aria-hidden='true'></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>

          <div className='contact-cta'>
            <p>
              <i className='fas fa-handshake' aria-hidden='true'></i>
              At Fahple, your questions and feedback matter. Reach out today and
              let's grow together!
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
